/**
 * Brand theme utility functions
 */

/**
 * Determines if the given brand theme represents a Rootly-branded organization
 * (ROOTLY white-label or ROOTLY_COBRAND co-brand). Mirrors the backend helper
 * `Organization.is_rootly_branded`.
 *
 * Note: ROOTLY_HYBRID is intentionally excluded — those orgs are TierZero
 * customers with Rootly visual styling on the UI only. Use the inline
 * "ROOTLY_HYBRID" check (or a dedicated logo helper) for cases that genuinely
 * need to apply Rootly UI styling to hybrid orgs.
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns true if the theme is ROOTLY or ROOTLY_COBRAND, false otherwise
 */
export function isRootlyBranded(brandTheme?: string | null): boolean {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND";
}

/**
 * Determines if the given brand theme is the full white-label ROOTLY theme
 * (which hides all TierZero branding references).
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns true if the theme is ROOTLY, false otherwise
 */
export function isRootlyWhiteLabel(brandTheme?: string | null): boolean {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY";
}

/**
 * Determines if Slack integration is managed by Rootly for the given brand theme.
 * This applies to ROOTLY and ROOTLY_COBRAND themes (not ROOTLY_HYBRID).
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns true if the theme is ROOTLY or ROOTLY_COBRAND, false otherwise
 */
export function isSlackManagedByRootly(brandTheme?: string | null): boolean {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND";
}

/**
 * Determines if the organization should only show Alerts (not Incidents) for Rootly webhooks.
 * This applies to ROOTLY and ROOTLY_COBRAND themes (same as isSlackManagedByRootly).
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns true if the theme is ROOTLY or ROOTLY_COBRAND, false otherwise
 */
export function shouldShowAlertsOnly(brandTheme?: string | null): boolean {
  return isSlackManagedByRootly(brandTheme);
}

/**
 * Determines if the current hostname is a Rootly-branded domain.
 * This is useful for detecting Rootly branding before authentication
 * when org metadata is not yet available.
 *
 * @returns true if the current hostname is a Rootly domain (e.g., ai-sre.rootly.com)
 */
export function isRootlyDomain(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "ai-sre.rootly.com";
}

function isFirstPartyDomain(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  // eslint-disable-next-line custom/no-hardcoded-brand-name -- branding source of truth
  if (hostname.endsWith(".tierzero.ai")) return true;
  return false;
}

/**
 * Infers a brand theme from the current hostname for pre-auth pages
 * where org metadata is not yet available. Returns "TIERZERO" for
 * known first-party domains, "ROOTLY" for Rootly domains, and null
 * for unknown/on-prem domains so callers can render a generic UI.
 */
export function getBrandThemeFromDomain(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname;
  if (hostname === "ai-sre.rootly.com") return "ROOTLY";
  if (isFirstPartyDomain(hostname)) return "TIERZERO";
  return null;
}

/**
 * Returns the knowledge base path for the agent markdown file.
 * ROOTLY and ROOTLY_COBRAND orgs use /knowledge/agent.md, others use /knowledge/tierzero.md.
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns The path to the knowledge markdown file
 */
export function getKnowledgeBasePath(brandTheme?: string | null): string {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND"
    ? "/knowledge/agent.md"
    : "/knowledge/tierzero.md";
}

/**
 * Determines if usage credits should be hidden for the given brand theme.
 * ROOTLY and ROOTLY_COBRAND orgs should not see usage credits in chat, alert responder, or prompt studio.
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns true if usage credits should be hidden, false otherwise
 */
export function shouldHideUsageCredits(brandTheme?: string | null): boolean {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND";
}

/**
 * Returns the product name based on the brand theme.
 * ROOTLY and ROOTLY_COBRAND use "Rootly AI SRE", others use "TierZero".
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns The product name to display
 */
export function getProductName(brandTheme?: string | null): string {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND" ? "Rootly AI SRE" : "TierZero";
}

/**
 * Returns the bot name for Slack @mentions based on the brand theme.
 * ROOTLY and ROOTLY_COBRAND use "Rootly", others use "TierZero".
 * This is distinct from getProductName() which returns the full product name.
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns The bot name for Slack @mentions
 */
export function getBotName(brandTheme?: string | null): string {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND" ? "Rootly" : "TierZero";
}

/**
 * Returns the knowledge file display name based on the brand theme.
 * ROOTLY and ROOTLY_COBRAND use "Agent.md", others use "TierZero.md".
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns The knowledge file display name
 */
/**
 * Returns the support email based on the brand theme.
 * ROOTLY and ROOTLY_COBRAND use "support@rootly.com", others use "support@tierzero.ai".
 *
 * @param brandTheme - The brand theme to check (defaults to "TIERZERO" if null/undefined)
 * @returns The support email address
 */
export function getSupportEmail(brandTheme?: string | null): string {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND" ? "support@rootly.com" : "support@tierzero.ai";
}

/**
 * Returns the documentation base URL for the given brand theme.
 * Returns null for ROOTLY/ROOTLY_COBRAND (no docs site exists for Rootly AI SRE).
 */
export function getDocsUrl(brandTheme?: string | null): string | null {
  const theme = brandTheme ?? "TIERZERO";
  if (theme === "ROOTLY" || theme === "ROOTLY_COBRAND") return null;
  // eslint-disable-next-line custom/no-hardcoded-brand-name -- branding source of truth
  return "https://docs.tierzero.ai";
}

export function getKnowledgeFileName(brandTheme?: string | null): string {
  const theme = brandTheme ?? "TIERZERO";
  return theme === "ROOTLY" || theme === "ROOTLY_COBRAND" ? "Agent.md" : "TierZero.md";
}
